/**
 * Utility functions for formatting various data types
 */

/**
 * Format a number as currency (South African Rand)
 * @param value - The numeric value to format
 * @returns Formatted currency string
 */
export function formatCurrency(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00';
  }
  
  return value.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format data usage in MB with appropriate units
 * @param value - The value in MB
 * @returns Formatted data usage string
 */
export function formatDataUsage(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0 MB';
  }

  if (value >= 1024) {
    const gb = value / 1024;
    return `${gb.toFixed(2)} GB`;
  }
  
  return `${value.toFixed(2)} MB`;
}

/**
 * Format airtime balance
 * @param value - The airtime value in Rand
 * @returns Formatted airtime string
 */
export function formatAirtime(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R0.00';
  }
  
  return `R${formatCurrency(value)}`;
}

/**
 * Format a percentage value
 * @param value - The percentage value (0-100)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  
  return `${value.toFixed(1)}%`;
}

/**
 * Format a date for display
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date | string): string {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return 'Invalid Date';
  
  return d.toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a date and time for display
 * @param date - The date to format
 * @returns Formatted date and time string
 */
export function formatDateTime(date: Date | string): string {
  if (!date) return 'N/A';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(d.getTime())) return 'Invalid Date';
  
  return d.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a number with thousands separators
 * @param value - The numeric value to format
 * @returns Formatted number string
 */
export function formatNumber(value: number): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  return value.toLocaleString('en-ZA');
}

/**
 * Check if a product is an expiry product (has 1 Day, 7 Day, or 30 Day in the name)
 * @param productName - The product name to check
 * @returns True if the product is an expiry product
 */
export function isExpiryProduct(productName: string): boolean {
  if (!productName) return false;
  
  // Check for various day patterns in the product name
  const expiryPatterns = [
    '1 Day',
    '7 Day', 
    '30 Day',
    '1 day',
    '7 day',
    '30 day',
    '1Day',
    '7Day',
    '30Day'
  ];
  
  return expiryPatterns.some(pattern => productName.includes(pattern));
}

/**
 * Filter products to exclude expiry products (non-expiry products only)
 * @param products - Array of products to filter
 * @param shouldFilter - Whether to apply the filter (defaults to true)
 * @returns Array of products (filtered or unfiltered based on shouldFilter)
 */
export function filterNonExpiryProducts<T extends { name: string }>(
  products: T[], 
  shouldFilter: boolean = true
): T[] {
  if (!shouldFilter) {
    return products;
  }
  return products.filter(product => !isExpiryProduct(product.name));
} 